import React, { useContext, useEffect, useRef, useState } from 'react';
import { Functionality, API_BASE } from '../context/Functionality';

export default function AutomationTokens() {
    const { token } = useContext(Functionality);
    const [records, setRecords] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [name, setName] = useState('');
    const [days, setDays] = useState('90');
    const [publish, setPublish] = useState(false);
    const [secret, setSecret] = useState('');
    const [notice, setNotice] = useState('');
    const lock = useRef(false);
    const current = useRef(0);
    async function request(path = '', method = 'GET', body) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);
        try {
            const response = await fetch(`${API_BASE}/api/automation-tokens${path}`, {
                signal: controller.signal, method, cache: 'no-store', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                ...(body ? { body: JSON.stringify(body) } : {})
            });
            const data = await response.json().catch(failure => {
                if (failure.name === 'AbortError') throw failure;
                if (response.ok) throw new Error('The server returned an unexpected response. Refresh the list before retrying.');
                return {};
            });
            if (!response.ok) {
                const message = data.errMsg || 'The request failed. Please try again.';
                throw new Error(`${message}${response.status === 429 ? ` Retry in ${response.headers.get('Retry-After') || '60'} seconds.` : ''}${data.requestId ? ` Reference: ${data.requestId}` : ''}`);
            }
            const validRecord = value => value && typeof value._id === 'string' && typeof value.name === 'string' && Array.isArray(value.permissions);
            const valid = method === 'GET' ? Array.isArray(data) && data.every(validRecord)
                : validRecord(data) && (path ? Boolean(data.revokedAt) : /^bt_blog_[a-f0-9]{64}$/.test(data.token || ''));
            if (!valid) throw new Error('The server returned an unexpected response. Refresh the list before retrying.');
            return data;
        } catch (failure) {
            if (failure.name === 'AbortError') throw new Error('The request timed out. Please try again.');
            throw failure;
        } finally { clearTimeout(timeout); }
    }
    async function load(generation = current.current) {
        setError('');
        try { const data = await request(); if (generation === current.current) setRecords(data); }
        catch (failure) { if (generation === current.current) setError(failure.message); }
    }
    useEffect(() => {
        const generation = ++current.current;
        setRecords(null); setSecret(''); setNotice(''); setBusy(false);
        load(generation);
        return () => { current.current++; };
        // Reset all sensitive state when the authenticated session changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);
    async function mutate(action) {
        if (lock.current) return;
        lock.current = true; setBusy(true); setError(''); setNotice('');
        const generation = current.current;
        try { await action(generation); }
        catch (failure) { if (generation === current.current) setError(failure.message); }
        finally { lock.current = false; if (generation === current.current) setBusy(false); }
    }
    function create(event) {
        event.preventDefault();
        mutate(async generation => {
            let data;
            try {
                data = await request('', 'POST', { name, permissions: publish ? ['blog:draft', 'blog:publish'] : ['blog:draft'], expiresAt: new Date(Date.now() + Number(days) * 86400000).toISOString() });
            } catch (failure) {
                throw new Error(`${failure.message} If the response was interrupted, refresh the list and revoke any token you did not receive before creating a replacement.`);
            }
            if (generation !== current.current) return;
            const { token: raw, ...record } = data;
            setSecret(raw); setRecords(previous => [record, ...previous]); setName('');
            setNotice('Token created. Copy it now; it cannot be retrieved later.');
        });
    }
    function revoke(record) {
        if (!window.confirm(`Revoke “${record.name}”? Automation using this token will stop working.`)) return;
        mutate(async generation => {
            const data = await request(`/${record._id}/revoke`, 'POST');
            if (generation !== current.current) return;
            setRecords(previous => previous.map(item => item._id === data._id ? data : item));
            if (secret.startsWith(record.prefix)) setSecret('');
            setNotice('Token revoked.');
        });
    }
    async function copy() {
        try { await navigator.clipboard.writeText(secret); setNotice('Token copied. Store it securely outside the repository.'); }
        catch { setError('Clipboard access failed. Select and copy the token manually.'); }
    }
    return <section className="max-w-3xl mx-auto p-4 space-y-4">
        <h1 className="text-2xl font-bold">Blog automation tokens</h1>
        <p>Tokens can access only articles they create. Draft access is included; publishing is optional. Tokens cannot delete articles or access other portal data.</p>
        {error && <div role="alert">{error} {records === null && <button type="button" onClick={() => load()} className="underline">Retry</button>}</div>}
        {notice && <p role="status">{notice}</p>}
        {records === null && !error && <p>Loading tokens…</p>}
        {records !== null && <>
            {secret && <div className="border rounded p-4 space-y-2">
                <label className="block" htmlFor="new-token">New token — shown only this time</label>
                <input id="new-token" readOnly value={secret} className="w-full border p-2 font-mono" autoComplete="off" spellCheck={false} />
                <button type="button" onClick={copy} className="border rounded p-2 mr-2">Copy token</button>
                <button type="button" onClick={() => setSecret('')} className="border rounded p-2">Dismiss token</button>
                <p>If you lose it, revoke it and create a replacement.</p>
            </div>}
            <form onSubmit={create} className="space-y-3">
                <fieldset disabled={busy || Boolean(secret)} className="space-y-3">
                    <label className="block">Token name<input required maxLength={100} value={name} onChange={event => setName(event.target.value)} className="block w-full border rounded p-2" /></label>
                    <label className="block">Expires in<select value={days} onChange={event => setDays(event.target.value)} className="block border rounded p-2"><option value="30">30 days</option><option value="90">90 days</option><option value="365">365 days</option></select></label>
                    <p>Permission: create and edit its own drafts.</p>
                    <label className="block"><input type="checkbox" checked={publish} onChange={event => setPublish(event.target.checked)} /> Allow publishing its own articles</label>
                    <button className="border rounded p-2" type="submit">{busy ? 'Saving…' : 'Create token'}</button>
                </fieldset>
            </form>
            <h2 className="text-xl font-semibold">Existing tokens</h2>
            <button type="button" disabled={busy} onClick={() => mutate(async () => { await load(); })} className="border rounded p-2">Refresh list</button>
            {!records.length && <p>No automation tokens yet.</p>}
            <ul className="space-y-3">{records.map(record => <li key={record._id} className="border rounded p-4 break-words">
                <h3 className="font-bold">{record.name}</h3>
                <p>{record.prefix}… · {record.revokedAt ? 'Revoked' : new Date(record.expiresAt) <= new Date() ? 'Expired' : 'Active'}</p>
                <p>{record.permissions.includes('blog:publish') ? 'Drafts and publishing' : 'Drafts only'}</p>
                <p>Expires: {new Date(record.expiresAt).toLocaleString()}</p>
                <p>Last used: {record.lastUsedAt ? new Date(record.lastUsedAt).toLocaleString() : 'Never'}</p>
                {!record.revokedAt && <button disabled={busy} type="button" onClick={() => revoke(record)} className="border rounded p-2 mt-2">Revoke {record.name}</button>}
            </li>)}</ul>
        </>}
    </section>;
}
